import type { RouteLocationNormalized } from 'vue-router';

import { getOrCreateSessionId } from '~/composables/visitorIdentity/utils';

export const VIEWED_SHORTS_STORAGE_KEY = 'viewed_shorts_session';

export const VIEWED_SHORTS_TTL_MS = 30 * 60 * 1000;

const SHORTS_ROUTE_NAMES = new Set<RouteLocationNormalized['name']>([
	'shorts-commercialId',
	'shorts-home',
	'shorts-home-segments'
]);

interface ViewedShortsMark {
	sessionId: string;
	viewedAt: number;
}

function readMark(): ViewedShortsMark | null {
	const raw = localStorage.getItem(VIEWED_SHORTS_STORAGE_KEY);
	if (!raw) return null;

	const mark = JSON.parse(raw) as Partial<ViewedShortsMark> | null;
	if (typeof mark?.sessionId !== 'string') return null;
	if (typeof mark.viewedAt !== 'number') return null;

	return { sessionId: mark.sessionId, viewedAt: mark.viewedAt };
}

export function markShortsViewedInSession(): void {
	if (typeof window === 'undefined') return;

	try {
		const mark: ViewedShortsMark = {
			sessionId: getOrCreateSessionId(),
			viewedAt: Date.now()
		};
		localStorage.setItem(VIEWED_SHORTS_STORAGE_KEY, JSON.stringify(mark));
	} catch {
		/* quota exceeded or private mode — non-critical */
	}
}

export function hasViewedShortsInSession(): boolean {
	if (typeof window === 'undefined') return false;

	try {
		const mark = readMark();
		if (!mark) return false;

		const isSameSession = mark.sessionId === getOrCreateSessionId();
		const isWithinTtl = Date.now() - mark.viewedAt <= VIEWED_SHORTS_TTL_MS;
		return isSameSession && isWithinTtl;
	} catch {
		/* blocked storage or corrupted mark — treat as not viewed */
		return false;
	}
}

export function captureViewedShortsFromRoute(
	route: Pick<RouteLocationNormalized, 'name'>
): void {
	if (!SHORTS_ROUTE_NAMES.has(route.name)) return;

	markShortsViewedInSession();
}
